import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Context } from '@front10/helpers/dist/Context';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import Button from '../../elements/Button';
import Icon from '../../elements/Icon';
import FareSimpleSorting from '../../sorting/FareSimpleSorting';
import BudgetFilter from '../Budget';
import StopsFilter from '../StopsFilter';
import TravelClassFilter from './TravelClassFilter';
import FlightTypeFilter from './FlightTypeFilter';
import OriginDestinationFilter from './OriginDestinationFilter';
import AirlinesFilter from './AirlinesFilter';
import { isNotNil, isNilOrEmpty } from '../../../helpers/utils';
import SingleDatePicker from '../DatePickerWidget/SingleDatePicker';
import SingleDateRangePicker from '../DatePickerWidget/SingleDateRangePicker';
import TravelerInterest from './TravelerInterest';

const InlineFilters = React.forwardRef((props, givenRef) => {
  const {
    clearButtonDisplay,
    sortingDisplay,
    showLabels,
    locations,
    dateRange,
    budget: { currencyCode, ...budget },
    travelClass,
    flightType,
    flightStopCount,
    sorting,
    onClear,
    availableFilters,
    containerSettings,
    clearAllSettings: { showIcon, icon: clearAllIcon, ...clearAllSettings },
    clearAllColumnSettings,
    originDestinationSettings,
    travelClassSettings,
    travelClassColumnSettings,
    flightTypeSettings,
    flightTypeColumnSettings,
    dateRangeSettings,
    dateRangeColumnSettings,
    flightStopCountSettings,
    flightStopCountColumnSettings,
    budgetSettings,
    budgetColumnSettings,
    sortingSettings,
    sortingColumnSettings,
    sortingRowSettings,
    airlinesSettings,
    airlines,
    airlinesColumnSettings,
    travelerInterest,
    travelerInterestSettings,
    travelerInterestColumnSettings
  } = props;
  const { translate } = React.useContext(Context);
  const ownRef = useRef();
  const ref = givenRef || ownRef;
  const originRef = useRef();
  const [wasClear, setWasClear] = useState(false);
  const locationComponents = {
    ...originDestinationSettings.components,
    origin: {
      // inputFocusReferences: originRef,
      ...originDestinationSettings.components?.origin
    }
  };

  const [dirtyDateSelector, setDirtyDateSelector] = useState(false);

  const isDirty = !!(
    locations.origin.value ||
    locations.destination.value ||
    dateRange.value.start ||
    dateRange.value.end ||
    travelClass.isChanged ||
    isNotNil(budget.value) ||
    dirtyDateSelector === true ||
    !isNilOrEmpty(travelerInterest?.selectedItems)
  );

  const elementsWrappers = {
    Airlines: (e) => (
      <Col key="Airlines" {...airlinesColumnSettings}>
        {e}
      </Col>
    ),
    OriginDestination: (e) => {
      return e;
    },
    TravelClass: (e) => (
      <Col key="TravelClass" {...travelClassColumnSettings}>
        {e}
      </Col>
    ),
    FlightType: (e) => (
      <Col key="FlightType" {...flightTypeColumnSettings}>
        {e}
      </Col>
    ),
    SingleDatePicker: (e) => (
      <Col key="SingleDatePicker" {...dateRangeColumnSettings}>
        {e}
      </Col>
    ),
    SingleDateRangePicker: (e) => (
      <Col key="SingleDateRangePicker" {...dateRangeColumnSettings}>
        {e}
      </Col>
    ),
    FlightStopCount: (e) => (
      <Col key="FlightStopCount" {...flightStopCountColumnSettings}>
        {e}
      </Col>
    ),
    Budget: (e) => (
      <Col key="Budget" {...budgetColumnSettings}>
        {e}
      </Col>
    ),
    Sorting: (e) => (
      <Col key="Sorting" {...sortingColumnSettings}>
        {e}
      </Col>
    ),
    TravelerInterest: (e) => (
      <Col key="TravelerInterest" {...travelerInterestColumnSettings}>
        {e}
      </Col>
    )
  };

  const elements = {
    Airlines: (
      <AirlinesFilter
        {...airlinesSettings}
        {...airlines}
        origin={locations.origin}
        destination={locations.destination}
        isDirty={wasClear}
        setWasClear={setWasClear}
      />
    ),
    OriginDestination: (
      <OriginDestinationFilter
        key="OriginDestination"
        showLabels={showLabels}
        {...originDestinationSettings}
        components={locationComponents}
        {...locations}
        isDirty={wasClear}
        setWasClear={setWasClear}
      />
    ),
    TravelClass: (
      <TravelClassFilter
        showLabel={showLabels}
        {...travelClassSettings}
        {...travelClass}
        isDirty={wasClear}
        setWasClear={setWasClear}
      />
    ),
    FlightType: (
      <FlightTypeFilter
        showLabel={showLabels}
        {...flightTypeSettings}
        {...flightType}
        isDirty={wasClear}
        setWasClear={setWasClear}
      />
    ),
    SingleDatePicker: (
      <SingleDatePicker
        showLabel={showLabels}
        {...dateRangeSettings}
        {...dateRange}
        onChange={(date) => {
          dateRange.onChange({
            start: date.start,
            type: 'departure',
            flexibleDates: date.flexibleDates,
            availableDays: date.availableDays,
            checkedFlexibleDates: date.checkedFlexibleDates
          });
        }}
        setDirtyDateSelector={setDirtyDateSelector}
        defaultValue={dateRange.value.start}
      />
    ),
    SingleDateRangePicker: (
      <SingleDateRangePicker
        showLabel={showLabels}
        {...dateRangeSettings}
        {...dateRange}
        onChange={(date) => {
          dateRange.onChange({
            start: date.start,
            end: date.end,
            flexibleDates: date.flexibleDates,
            availableDays: date.availableDays,
            checkedFlexibleDates: date.checkedFlexibleDates
          });
        }}
        defaultValue={dateRange.value}
      />
    ),
    Stops: (
      <StopsFilter
        showLabel={showLabels}
        {...flightStopCountSettings}
        {...flightStopCount}
        dataTestId="stopsFilter"
      />
    ),
    Budget: (
      <BudgetFilter
        showLabel={showLabels}
        currencyCode={currencyCode}
        {...budget}
        {...budgetSettings}
      />
    ),
    Sorting: <FareSimpleSorting {...sortingSettings} {...sorting} dataTestId="fareSimpleSorting" />,
    TravelerInterest: (
      <TravelerInterest {...travelerInterest} isDirty={wasClear} setWasClear={setWasClear} />
    )
  };
  const filtersToDisplay = availableFilters.filter((e) => {
    let iterator = e;
    if (typeof iterator === 'object') {
      iterator = e.type;
    }
    return elements[iterator] && (sortingDisplay === 'inline' || iterator !== 'Sorting');
  });

  const originFocus = () => {
    originRef.current?.focus();
  };

  React.useImperativeHandle(ref, () => ({
    originFocus
  }));

  const handleClear = () => {
    setWasClear(true);
    if (elementRefDatePicker.current?.reset !== undefined) {
      elementRefDatePicker.current.reset();
    }
    if (filtersToDisplay.indexOf('Budget') && firstElementRefBudget?.current?.reset !== undefined) {
      firstElementRefBudget?.current?.reset();
    }
    if (firstElementRef?.current?.toRef !== undefined) {
      firstElementRef.current.clear();
      firstElementRef.current.toRef.current.focus();
    } else if (firstElementRef?.current?.fromRef !== undefined) {
      firstElementRef.current.clear();
      firstElementRef.current.fromRef.current.focus();
    } else if (firstElementRef?.current?.focusInput !== undefined) {
      firstElementRef.current.focusInput();
      firstElementRef.current.clear();
    } else if (firstElementRef?.current?.focusedElement !== undefined) {
      firstElementRef?.current?.focusedElement();
      firstElementRef?.current?.reset();
    } else {
      setTimeout(() => firstElementRef.current?.focus(), 100);
    }
    onClear();
  };

  const ClearButton = () => (
    <Col sm="12" textAlign="right" {...clearAllColumnSettings}>
      <Button
        paddingWidth="0"
        textTransform="uppercase"
        fontSize="3"
        onClick={handleClear}
        disabled={!isDirty}
        cursor={isDirty ? 'pointer' : 'default'}
        dataTestId="clearAllBtn"
        display="inline-flex"
        alignItems="center"
        ariaLabel={translate('cmp-filter-clear-button-accessibility-message', 'Clear all')}
        {...clearAllSettings}
      >
        {translate('cmp-filter-clear', 'Clear all')}
        {showIcon && <Icon icon="close" paddingLeft="1" size={18} {...clearAllIcon} />}
      </Button>
    </Col>
  );

  const firstElementRef = useRef(null);
  const firstElementRefBudget = useRef(null);
  const elementRefDatePicker = useRef(null);
  return (
    <>
      <Row gutters marginBottom="2" {...containerSettings} focusReferences={ref}>
        {clearButtonDisplay === 'start' && <ClearButton />}
        {filtersToDisplay.map((ele, index) => {
          let e = ele;
          if (typeof e === 'object') {
            e = e.type;
          }
          const wrapper = elementsWrappers[e];
          const element = wrapper(elements[e]);
          if (index === 0) {
            let temporalElement;
            if (e === 'OriginDestination') {
              if (props.originDestinationSettings.showOrigin) {
                temporalElement = React.cloneElement(elements[e], {
                  components: {
                    ...locationComponents,
                    origin: {
                      ...locationComponents.origin,
                      inputFocusReferences: firstElementRef
                    }
                  }
                });
              } else if (props.originDestinationSettings.showDestination) {
                temporalElement = React.cloneElement(elements[e], {
                  components: {
                    ...locationComponents,
                    destination: {
                      ...locationComponents.destination,
                      inputFocusReferences: firstElementRef
                    }
                  }
                });
              } else {
                temporalElement = React.cloneElement(elements[e], {
                  components: {
                    ...locationComponents,
                    origin: {
                      ...locationComponents.origin,
                      inputFocusReferences: firstElementRef
                    }
                  }
                });
              }
            } else {
              if (e === 'SingleDatePicker' || e === 'SingleDateRangePicker') {
                temporalElement = React.cloneElement(elements[e], {
                  ref: elementRefDatePicker
                });
                firstElementRef.current = { ...elementRefDatePicker.current };
                return wrapper(temporalElement);
              }
              if (e === 'TravelerInterest') {
                temporalElement = React.cloneElement(elements[e], {
                  ref: firstElementRef,
                  ...travelerInterestSettings[ele.id]
                });
                return wrapper(temporalElement);
              }
              temporalElement = React.cloneElement(elements[e], {
                ref: firstElementRef
              });
            }
            return wrapper(temporalElement);
          } else {
            let temporalElement;
            if (e === 'Budget') {
              temporalElement = React.cloneElement(elements[e], {
                ref: firstElementRefBudget
              });
              return wrapper(temporalElement);
            }
            if (e === 'TravelerInterest') {
              temporalElement = React.cloneElement(elements[e], {
                // ref: firstElementRef,
                ...travelerInterestSettings[ele.id]
              });
              return wrapper(temporalElement);
            }
            if (e === 'SingleDatePicker' || e === 'SingleDateRangePicker') {
              temporalElement = React.cloneElement(elements[e], {
                ref: elementRefDatePicker
              });
              return wrapper(temporalElement);
            }
            return element;
          }
        })}
        {clearButtonDisplay === 'end' && <ClearButton />}
      </Row>
      {sortingDisplay === 'below-filters' && (
        <Row gutters marginBottom="2" {...sortingRowSettings}>
          {elements['Sorting']}
        </Row>
      )}
    </>
  );
});

InlineFilters.displayName = 'InlineFilters';

InlineFilters.propTypes = {
  clearButtonDisplay: PropTypes.oneOf(['start', 'end', 'hidden']),
  sortingDisplay: PropTypes.oneOf(['inline', 'below-filters']),
  showLabels: PropTypes.bool,
  availableFilters: PropTypes.arrayOf(
    PropTypes.oneOf([
      'OriginDestination',
      'SingleDateRangePicker',
      'SingleDatePicker',
      'TravelClass',
      'Stops',
      'Budget',
      'FlightType',
      'Sorting',
      'Airlines',
      'TravelerInterest'
    ])
  ),
  containerSettings: PropTypes.objectOf(PropTypes.any),
  clearAllSettings: PropTypes.objectOf(PropTypes.any),
  clearAllColumnSettings: PropTypes.objectOf(PropTypes.any),
  originDestinationSettings: PropTypes.objectOf(PropTypes.any),
  airlinesColumnSettings: PropTypes.objectOf(PropTypes.any),
  travelClassSettings: PropTypes.objectOf(PropTypes.any),
  travelClassColumnSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeSettings: PropTypes.objectOf(PropTypes.any),
  flightTypeColumnSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeSettings: PropTypes.objectOf(PropTypes.any),
  dateRangeColumnSettings: PropTypes.objectOf(PropTypes.any),
  stopSettings: PropTypes.objectOf(PropTypes.any),
  stopColumnSettings: PropTypes.objectOf(PropTypes.any),
  budgetSettings: PropTypes.objectOf(PropTypes.any),
  budgetColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingSettings: PropTypes.objectOf(PropTypes.any),
  sortingColumnSettings: PropTypes.objectOf(PropTypes.any),
  sortingRowSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Filters state and actions
   */
  locations: PropTypes.shape({
    origin: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    destination: PropTypes.shape({
      value: PropTypes.string,
      isLoading: PropTypes.bool,
      isError: PropTypes.bool,
      data: PropTypes.arrayOf(PropTypes.any),
      onClear: PropTypes.func,
      onChange: PropTypes.func
    }),
    swapLocations: PropTypes.func
  }),
  dateRange: PropTypes.shape({
    value: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    defaultValue: PropTypes.shape({
      start: PropTypes.instanceOf(Date),
      end: PropTypes.instanceOf(Date)
    }),
    onChange: PropTypes.func,
    onClear: PropTypes.func
  }),
  budget: PropTypes.shape({
    value: PropTypes.number,
    defaultValue: PropTypes.number,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    currencyCode: PropTypes.string
  }),
  travelClass: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  flightType: PropTypes.shape({
    defaultValue: PropTypes.string,
    onChange: PropTypes.func
  }),
  stops: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func
  }),
  sorting: PropTypes.shape({
    selectedOption: PropTypes.string,
    onChange: PropTypes.func
  }),
  onClear: PropTypes.func
};

InlineFilters.defaultProps = {
  clearButtonDisplay: 'start',
  sortingDisplay: 'inline',
  availableFilters: ['OriginDestination', 'SingleDateRangePicker', 'Budget'],
  showLabels: false,
  containerSettings: {
    padding: '1'
  },
  clearAllSettings: {
    color: 'link',
    bgColor: 'transparent',
    textColor: 'light-18',
    showIcon: true,
    borderNone: true,
    icon: {
      iconColor: 'light-18'
    }
  },
  clearAllColumnSettings: {},
  originDestinationSettings: {},
  airlinesColumnSettings: {},
  travelClassSettings: {},
  travelClassColumnSettings: {},
  flightTypeSettings: {},
  flightTypeColumnSettings: {},
  dateRangeSettings: {},
  dateRangeColumnSettings: {},
  stopSettings: {
    uiDisplay: {
      showLabel: false
    },
    height: '100',
    components: {
      select: {
        height: '100',
        selectStyles: { height: '100%' }
      }
    }
  },
  stopColumnSettings: {},
  budgetSettings: {},
  budgetColumnSettings: {},
  sortingSettings: {},
  sortingColumnSettings: {},
  sortingRowSettings: {},
  locations: { origin: {}, destination: {} },
  travelClass: {},
  flightType: {},
  dateRange: {},
  budget: {},
  stops: {},
  sorting: {},
  onClear: null
};

export default InlineFilters;
